import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Edit2, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Textarea } from '@/components/ui/textarea';
export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    emoji: '📢',
    type: 'info',
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/admin/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      logger.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editing) {
        await api.put(`/admin/announcements/${editing.id}`, formData);
        toast.success('Announcement updated!');
      } else {
        await api.post('/admin/announcements', formData);
        toast.success('Announcement created!');
      }
      
      fetchAnnouncements();
      handleCloseModal();
    } catch (error) {
      logger.error('Error saving announcement:', error);
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setEditing(announcement);
    setFormData({
      message: announcement.message,
      emoji: announcement.emoji,
      type: announcement.type,
      start_date: announcement.start_date || '',
      end_date: announcement.end_date || '',
      is_active: announcement.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/announcements/${deleteId}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      logger.error('Error deleting announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await api.put(`/admin/announcements/${announcement.id}`, {
        ...announcement,
        is_active: !announcement.is_active
      });
      toast.success(announcement.is_active ? 'Announcement hidden' : 'Announcement activated');
      fetchAnnouncements();
    } catch (error) {
      logger.error('Error toggling announcement:', error);
      toast.error('Failed to update announcement');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      message: '',
      emoji: '📢',
      type: 'info',
      start_date: '',
      end_date: '',
      is_active: true
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'warning': return '#F59E0B';
      case 'success': return '#10B981';
      case 'info': default: return '#3B82F6';
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            Announcements
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage site-wide announcement banners
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 w-full sm:w-auto"
          style={{ background: '#9E6752', color: 'white' }}
        >
          <Plus className="w-4 h-4" />
          <span className="sm:inline">New Announcement</span>
        </Button>
      </div>

      {announcements.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
          <p className="text-gray-500 mb-4">No announcements yet</p>
          <Button
            onClick={() => setShowModal(true)}
            style={{ background: '#9E6752', color: 'white' }}
          >
            Create First Announcement
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className="bg-white rounded-xl p-6 shadow-sm border"
              style={{ borderLeft: `4px solid ${getTypeColor(announcement.type)}` }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {announcement.emoji && (
                      <span className="text-2xl">{announcement.emoji}</span>
                    )}
                    <span
                      className="text-xs px-2 py-1 rounded-full"
                      style={{
                        background: getTypeColor(announcement.type) + '20',
                        color: getTypeColor(announcement.type)
                      }}
                    >
                      {announcement.type}
                    </span>
                    {!announcement.is_active && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                        Hidden
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-2">{announcement.message}</p>
                  <div className="flex gap-4 text-xs text-gray-500">
                    {announcement.start_date && (
                      <span>Start: {new Date(announcement.start_date).toLocaleDateString()}</span>
                    )}
                    {announcement.end_date && (
                      <span>End: {new Date(announcement.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggleActive(announcement)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                    title={announcement.is_active ? 'Hide' : 'Show'}
                  >
                    {announcement.is_active ? (
                      <Eye className="w-4 h-4 text-gray-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(announcement)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => setDeleteId(announcement.id)}
                    className="p-2 rounded-lg hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl admin-area">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editing ? 'Edit Announcement' : 'Create Announcement'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={3}
                placeholder="Enter announcement message..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Emoji</Label>
                <Input
                  value={formData.emoji}
                  onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
                  placeholder="📢"
                  maxLength={10}
                />
                <p className="text-xs text-gray-500 mt-1">Paste any emoji</p>
              </div>

              <div>
                <Label>Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (Blue)</SelectItem>
                    <SelectItem value="warning">Warning (Yellow)</SelectItem>
                    <SelectItem value="success">Success (Green)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Start Date (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>

              <div>
                <Label>End Date (Optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <Label htmlFor="is_active">Active (show on site)</Label>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                className="flex-1 order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 order-1 sm:order-2"
                style={{ background: '#9E6752', color: 'white' }}
              >
                {editing ? 'Update' : 'Create'} Announcement
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Announcement?"
        description="This announcement will be permanently removed from the site."
        confirmText="Delete"
      />
    </div>
  );
}
